import axios from 'axios';
import {
  showApiErrorModal,
  extractServerMessage,
} from '@/lib/apiErrorModal';

// Base URL for the CHANAKYA ERP backend. Sourced from environment only.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Request timeout (ms). Tunable via env var so heavy reports don't need code
// changes. Default 5 min — comfortably covers cold-start .NET calls and
// mid-size responses.
const API_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 300000;

// Standard timeout for report endpoints. Reports process large datasets on
// the server, so they get a longer window than regular calls. Import this in
// each report service and pass it as { timeout: REPORT_TIMEOUT_MS } on the
// request — override on a per-report basis only when explicitly required.
export const REPORT_TIMEOUT_MS = Number(process.env.REACT_APP_REPORT_TIMEOUT_MS) || 1800000;

// Storage keys used by the auth layer.
export const AUTH_TOKEN_KEY = 'chanakya.auth.token';
export const AUTH_USER_KEY = 'chanakya.auth.user';
export const AUTH_REMEMBER_KEY = 'chanakya.auth.remember';

/*
 * Central Action-API Error Modal (PRD Universality Rule #15)
 * -----------------------------------------------------------
 * The ERP wraps every backend call in a JSON envelope of the form
 *   { function: "<fn>", data: { … }, … }
 * and signals failure via `response.data.success = false` (not HTTP
 * status). We inspect the outgoing `function` name to decide whether
 * a `success:false` response should surface as the global Error Modal
 * or should stay silent (background list / search).
 *
 * "Action" functions — triggered by explicit operator gestures (button
 * click, Enter on save, delete confirm, etc.) — match this regex.
 * Everything else defaults to background behaviour and does NOT open
 * the modal. Callers may also override explicitly via
 * `axiosConfig.actionApi = true/false`.
 */
const ACTION_FN_PATTERN =
  /^(save|delete|cancel|confirm|print|update|remove|approve|release|reject|generate|post|reset|revoke|assign|share|send|export|check|allocate|process|dispatch|receive|import|apply|verify)/i;

function isActionRequest(cfg) {
  if (!cfg) return false;
  if (cfg.actionApi === true) return true;
  if (cfg.actionApi === false) return false;
  try {
    const body = typeof cfg.data === 'string' ? JSON.parse(cfg.data) : cfg.data;
    const fn = String(body?.function || body?.fn || '').toLowerCase();
    if (!fn) return false;
    return ACTION_FN_PATTERN.test(fn);
  } catch {
    return false;
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  // The API is stateless: all data (including the session marker) comes back
  // in the JSON body. Cookies are not required, so we keep this off to allow
  // the server to use a simple `Access-Control-Allow-Origin: *` CORS policy.
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Attach a bearer token if the API ever returns one; harmless otherwise.
api.interceptors.request.use((config) => {
  // Offline guard — refuse action calls while the browser reports
  // no network. Read/list calls are still allowed to attempt (they
  // will fail fast with a network error, which the UI treats as a
  // stale-data situation rather than lost data).
  if (
    typeof navigator !== 'undefined' &&
    navigator.onLine === false &&
    isActionRequest(config)
  ) {
    showApiErrorModal(
      'You are offline. Please reconnect to the internet before saving.',
      { title: 'No Internet Connection' },
    );
    const err = new Error('OFFLINE');
    err.__offline    = true;
    err.serverMessage = 'You are offline. Please reconnect to the internet before saving.';
    return Promise.reject(err);
  }
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY)
      : null;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global forcelogout signal — any response with `forcelogout: true` fires
// a window CustomEvent that <LastActivityWatcher/> subscribes to. Keeping
// this centralised means every future endpoint gets the behaviour for free.
api.interceptors.response.use(
  (response) => {
    if (response?.data?.forcelogout === true && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('chanakya:forcelogout', {
          detail: {
            message:
              response.data?.message ||
              'Your session has been terminated by the administrator.',
          },
        }),
      );
    }
    // Central Action-API Error Modal — surface success:false server
    // errors for action endpoints (Save / Delete / Confirm / Print /
    // Check / …) without touching individual pages. Background list /
    // search responses skip this branch by design.
    if (
      response?.data?.success === false &&
      response?.data?.forcelogout !== true &&
      isActionRequest(response.config)
    ) {
      showApiErrorModal(response.data?.message || 'The server returned an error.');
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    if (status === 401 && typeof window !== 'undefined') {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      sessionStorage.removeItem(AUTH_USER_KEY);
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    } else if (isActionRequest(error?.config)) {
      // Network / 5xx / timeout on an action call — surface the same
      // modal so operators can copy the exact error for support.
      showApiErrorModal(extractServerMessage(error));
    }
    return Promise.reject(error);
  },
);

export default api;
